<template>
  <!-- The handshake offer as it opens between two players checked in at one spot: the pair, and the disc a finger taps. -->
  <div class="offer-sheet">
    <div class="offer-top">
      <span class="offer-faces" aria-hidden="true">
        <span class="offer-face is-me">
          <GamIcon :name="GamIconName.PAWN_EMPTY" size="22px" is-current />
        </span>
        <span class="offer-face">
          <img :src="faceSrc" alt="" />
        </span>
      </span>
      <span class="offer-name">{{ localize('auth.landing.offer.name') }}</span>
    </div>

    <button type="button" class="offer-button" :aria-label="localize('auth.landing.start')" @click="emit('play')">
      <span class="offer-disc" :class="{ 'is-live': active }">
        <GamIcon :name="GamIconName.HANDSHAKE" size="64px" />
      </span>
      <span v-if="active" class="offer-tap" aria-hidden="true">
        <GamIcon :name="GamIconName.TAP" size="56px" />
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';

defineProps<{
  /** The other player, checked in at the same spot. */
  faceSrc: string;
  /** The card is the one in front, so the tap starts again from its first beat. */
  active: boolean;
}>();

const emit = defineEmits<{ play: [] }>();
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/* The handshake sheet's board, with the offer's pair on top and its disc on the middle line. */
.offer-sheet {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  justify-items: center;
  align-items: center;
  box-sizing: border-box;
  width: min(100%, 420px);
  aspect-ratio: 1;
  padding: var(--spacing-h);
  overflow: hidden;
  border-radius: calc(36 / 420 * 100%);
  background-image: repeating-conic-gradient(var(--color-dark-700) 0% 25%, var(--color-dark-600) 0% 50%);
  background-size: 25% 25%;
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.45),
    0 18px 40px rgba(0, 0, 0, 0.55);
  container-type: size;
}

.offer-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: start;
  gap: var(--spacing-md);
}

.offer-faces {
  --face: 48px;
  display: flex;
}

.offer-face {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--face);
  height: var(--face);
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 14%;
    object-fit: contain;
  }

  & + & {
    margin-left: calc(var(--face) * -0.2);
    box-shadow: 0 0 0 2px var(--color-dark-700);
  }

  &.is-me {
    background: var(--color-not-played);
    color: var(--color-white-30);
  }
}

.offer-name {
  padding: var(--spacing-xxs) var(--spacing-l);
  border-radius: 32px;
  background: var(--color-dark-30);
  backdrop-filter: blur(15px);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 1.8;
  color: var(--color-white);
}

.offer-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

/* The offer's beat: three taps and a rest, the finger and the disc together. It starts when the card is in front. */
$tap-cycle: 3.6s;

.offer-disc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(160px, 40cqmin);
  aspect-ratio: 1;
  border-radius: 50%;
  background: var(--color-pink);
  @include raised-disc;

  &.is-live {
    animation: offer-knock $tap-cycle ease-in-out infinite;
  }

  :deep(svg *) {
    fill: var(--color-white) !important;
  }
}

.offer-button:active .offer-disc {
  animation: none;
  @include raised-disc-pressed;
}

@keyframes offer-knock {
  0%,
  16%,
  23%,
  28%,
  35%,
  40%,
  47%,
  100% {
    transform: scale(1);
  }

  19%,
  31%,
  43% {
    transform: scale(0.965);
  }
}

/* The fingertip lands a little below and right of the middle, so the handshake stays in sight under it. */
.offer-tap {
  position: absolute;
  top: calc(60% - 14px);
  left: calc(60% - 20px);
  display: flex;
  opacity: 0.95;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.45));
  transform-origin: 35% 25%;
  pointer-events: none;
  animation: offer-tap-finger $tap-cycle ease-in-out infinite;
}

@keyframes offer-tap-finger {
  0%,
  16%,
  23%,
  28%,
  35%,
  40%,
  47% {
    opacity: 0.95;
    transform: translate(0, 0) scale(1);
  }

  19%,
  31%,
  43% {
    opacity: 0.95;
    transform: translate(0, 3px) scale(0.88);
  }

  60%,
  100% {
    opacity: 0;
    transform: translate(10px, 10px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .offer-disc,
  .offer-disc.is-live {
    animation: none;
  }

  .offer-tap {
    opacity: 0.95;
    animation: none;
  }
}
</style>
